
import { supabase } from '@/integrations/supabase/client';
import { Product, globalProducts, mapProductToDatabase, mapDatabaseToProduct } from '@/types/product';

// Function to create a product
export const createProduct = async (productData: Omit<Product, 'id' | 'created_at' | 'status'>): Promise<Product | null> => {
  try {

    // 1. Get the current user from Supabase auth
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();
    
    if (authError || !user?.id) {
      throw new Error("User not authenticated or missing user ID");
    }

    const stripeResponse = await supabase.functions.invoke('create-stripe-product', {
      body: {
        name: productData.title,
        description: productData.description,
        price_cents: Math.round(productData.amount * 100),
        currency: 'usd',
        billing_period: productData.billing_period,
        status: 'active',
        is_test:import.meta.env.VITE_APP_ENV != "production",
        is_subscription: productData.billing_period !== 'one_time',
        user_id: user.id
      }
    });

    if (stripeResponse.error || !stripeResponse?.data?.success) {
      console.error('Error creating product in Stripe:', stripeResponse.error);
      return null;
    }

    const { stripeProduct, stripePrice } = stripeResponse.data;

    const productToInsert = mapProductToDatabase({
      ...productData,
      created_at: new Date().toISOString(),
      status: 'active',
      is_test: import.meta.env.VITE_APP_ENV != "production",
      stripe_product_id: stripeProduct.id,
      stripe_price_id: stripePrice.id,
    } as Product);
    
    // 2. Validate productToInsert and insert the product
    const { data, error } = await supabase
      .from('products')
      .insert({
        ...productToInsert,
        user_id: user.id, // Explicitly set the user_id to the current user's ID
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating product:', error);
      if (error.code === 'PGRST116') {
        console.error('❌ Row-level security prevented creating product');
      }
      return null;
    }

    if (data) {

      const newProduct = mapDatabaseToProduct(data);

      // Add to global cache
      globalProducts.push(newProduct);

      return newProduct;
    }

    return null;
  } catch (error) {
    console.error('Error in createProduct:', error);
    return null;
  }
};

// Helper function to get current user ID
const getCurrentUserId = async (): Promise<string | null> => {
  try {
    const { data } = await supabase.auth.getUser();
    return data.user?.id || null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// Add product function for compatibility
export const addProduct = async (product: Product): Promise<Product | null> => {
  try {
    // This is a simplified version just to fix the type error
    globalProducts.push(product);
    return product;
  } catch (error) {
    console.error('Error in addProduct:', error);
    return null;
  }
};

// Function to archive a product
export const archiveProduct = async (id: string, status: string): Promise<boolean> => {
  try {
    
    // First check if the user is logged in
    const userId = await getCurrentUserId();
    if (!userId) {
      console.error('❌ User not authenticated. Cannot archive product.');
      return false;
    }
    
    // With RLS enabled, this will only work if the product belongs to the user
    const { error } = await supabase
      .from('products')
      .update({ status: status })
      .eq('id', id);
    
    if (error) {
      console.error(`Error archiving product ${id}:`, error);
      if (error.code === 'PGRST116') {
        console.error('❌ Row-level security prevented archiving product');
      }
      return false;
    }
    
    // Update in global cache
    const productIndex = globalProducts.findIndex(p => p.id === id);
    if (productIndex >= 0) {
      globalProducts[productIndex].status = 'archived';
    } else {
      console.warn(`Product with ID ${id} not found in global cache to update status`);
    }
    
    return true;
  } catch (error) {
    console.error(`Error in archiveProduct for ${id}:`, error);
    return false;
  }
};
